
import React from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { Award, Users, Globe, Heart, CheckCircle } from 'lucide-react';
import MasterSEOController from '@/components/seo/MasterSEOController';
import ExpertiseAuthority from '@/components/seo/ExpertiseAuthority';

const About = () => {
  const { t, language, getLocalizedPath } = useLanguage();
  
  return (
    <>
      {/* Master SEO Controller */}
      <MasterSEOController 
        pageType="about"
        currentLanguage={language}
        customMetaTags={{
          title: "About Global Therapy Connect - Our Mission & Vision",
          description: "Learn about Global Therapy Connect, founded by Dr. José Luis Marín. We democratize mental health access worldwide, connecting patients with 5,000+ verified therapists across 150+ countries.",
          keywords: ['about therapy platform', 'mental health directory', 'Dr. José Luis Marín', 'global therapy network', 'psychotherapy training', 'brief psychotherapy', 'mental health mission']
        }}
        enabledFeatures={{
          multilingual: true,
          trustSignals: true
        }}
      />
      
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-brand-gray-900 mb-6">
              {t('aboutUs')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Democratizing mental health access worldwide through verified professionals and innovative technology
            </p>
          </div>
          
          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">5,000+</div>
              <div className="text-sm text-muted-foreground">Verified Therapists</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">150+</div>
              <div className="text-sm text-muted-foreground">Countries</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">40+</div>
              <div className="text-sm text-muted-foreground">Languages</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">100K+</div>
              <div className="text-sm text-muted-foreground">Sessions Completed</div>
            </div>
          </div>
          
          <Card className="mb-12 shadow-md">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl font-semibold text-brand-gray-800 mb-4">
                {t('ourMission')}
              </h2>
              <p className="text-brand-gray-600 mb-6 leading-relaxed">
                {t('ourMissionText')}
              </p>
              
              <h2 className="text-2xl font-semibold text-brand-gray-800 mb-4">
                {t('ourStory')}
              </h2>
              <p className="text-brand-gray-600 mb-6 leading-relaxed">
                {t('ourStoryText1')}
              </p>
              <p className="text-brand-gray-600 mb-6 leading-relaxed">
                {t('ourStoryText2')}
              </p>
              
              <h2 className="text-2xl font-semibold text-brand-gray-800 mb-4">
                {t('ourValues')}
              </h2>
              <ul className="text-brand-gray-600 mb-6 list-disc pl-5 space-y-2">
                <li className="leading-relaxed"><span className="font-medium">{t('accessibilityValue')}:</span> {t('accessibilityValueText')}</li>
                <li className="leading-relaxed"><span className="font-medium">{t('qualityValue')}:</span> {t('qualityValueText')}</li>
                <li className="leading-relaxed"><span className="font-medium">{t('confidentialityValue')}:</span> {t('confidentialityValueText')}</li>
                <li className="leading-relaxed"><span className="font-medium">{t('inclusivityValue')}:</span> {t('inclusivityValueText')}</li>
                <li className="leading-relaxed"><span className="font-medium">{t('innovationValue')}:</span> {t('innovationValueText')}</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-brand-gray-800 mb-4">
                {t('qualityAssurance')}
              </h2>
              <p className="text-brand-gray-600 mb-6 leading-relaxed">
                {t('qualityAssuranceText1')}
              </p>
              <p className="text-brand-gray-600 leading-relaxed">
                {t('qualityAssuranceText2')}
              </p>
            </CardContent>
          </Card>
          
          {/* Dr. José Luis Marín Section */}
          <Card className="mb-12 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center gap-4 mb-6">
                <Award className="w-12 h-12 text-primary" />
                <div>
                  <h2 className="text-3xl font-bold text-brand-gray-900">Dr. José Luis Marín López</h2>
                  <p className="text-lg text-muted-foreground">Founder & Chief Training Officer</p>
                </div>
              </div>
              <p className="text-brand-gray-700 mb-4 leading-relaxed">
                Renowned psychiatrist and psychotherapist with over 40 years of experience in mental health. Creator of Brief Psychotherapy methodology adopted internationally by thousands of mental health professionals.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Academic Excellence</div>
                    <div className="text-sm text-muted-foreground">Degree from Complutense University of Madrid, Specialization from University of Ghent</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">International Leadership</div>
                    <div className="text-sm text-muted-foreground">Former President of Spanish Society of Psychosomatic Medicine and Psychotherapy</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Research Pioneer</div>
                    <div className="text-sm text-muted-foreground">Groundbreaking work on chronic stress, trauma, and psychosomatic medicine</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Training Impact</div>
                    <div className="text-sm text-muted-foreground">Trained 30,000+ mental health professionals worldwide</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Core Values */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card>
              <CardContent className="pt-6">
                <Heart className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Our Mission</h3>
                <p className="text-muted-foreground leading-relaxed">
                  To democratize access to quality mental health care by connecting patients worldwide with certified therapists, making professional psychological support accessible, affordable, and culturally sensitive.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Globe className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Our Vision</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A world where mental health support is as accessible as physical health care, where language and geography are no barriers, and where every person can find a therapist who understands their unique needs.
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* E-E-A-T Component */}
          <ExpertiseAuthority 
            showFounderProfile={true}
            showCertifications={true}
            showStatistics={true}
            showQualityAssurance={true}
            pageType="about"
          />
          
          {/* CTA Section */}
          <div className="bg-primary/5 rounded-xl p-8 md:p-10 text-center mt-12">
            <h2 className="text-3xl font-bold text-brand-gray-900 mb-4">
              Join Our Global Community
            </h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Whether you're seeking support or providing it, you're part of a movement to make mental health care accessible to everyone, everywhere.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={getLocalizedPath('/find')}>
                <Button size="lg">
                  Find a Therapist
                </Button>
              </Link>
              <Link to={getLocalizedPath('/for-therapists')}>
                <Button size="lg" variant="outline">
                  Join as a Professional
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
